import { useCallback, useEffect, useMemo, useState } from "react";
import { SpritePlayer } from "codexpetlab-player";
import { deleteLocalPet, listLocalPets, saveLocalPet, type LocalPetRecord } from "../lib/local-pets";
import { validateLocalPetFiles } from "../lib/local-pet-validation";

export function LocalPetSprite({ pet, width = 140 }: { pet: LocalPetRecord; width?: number }) {
  const [src, setSrc] = useState("");
  useEffect(() => {
    const url = URL.createObjectURL(pet.spritesheet);
    setSrc(url);
    return () => URL.revokeObjectURL(url);
  }, [pet.spritesheet]);
  return src ? <SpritePlayer src={src} state="idle" width={width} aria-label={pet.manifest.displayName} /> : null;
}

export function PetManager({ activeId, onSelect }: { activeId: string | null; onSelect: (pet: LocalPetRecord | null) => void }) {
  const [pets, setPets] = useState<LocalPetRecord[]>([]);
  const [query, setQuery] = useState("");
  const [manifest, setManifest] = useState<File | null>(null);
  const [sheet, setSheet] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [steam, setSteam] = useState<SteamRuntimeStatus | null>(null);
  const [workshopItems, setWorkshopItems] = useState<SteamWorkshopInstall[]>([]);
  const [workshopTitle, setWorkshopTitle] = useState("");
  const [workshopDescription, setWorkshopDescription] = useState("");
  const [publishedFileId, setPublishedFileId] = useState("");
  const refreshPets = useCallback(async () => setPets(await listLocalPets()), []);
  useEffect(() => {
    void refreshPets().catch(() => setMessage("无法读取本地宠物库，请重新打开管理页。"));
    if (!window.desktopPet) return;
    void Promise.all([window.desktopPet.getSteamStatus(), window.desktopPet.getWorkshopItems()])
      .then(([status, items]) => { setSteam(status); setWorkshopItems(items); })
      .catch(() => setSteam({ available: false, appId: 5_215_690, error: "无法读取 Steam 状态" }));
  }, [refreshPets]);
  const filteredPets = useMemo(() => {
    const normalized = query.trim().toLocaleLowerCase();
    return normalized ? pets.filter((pet) => pet.manifest.displayName.toLocaleLowerCase().includes(normalized)) : pets;
  }, [pets, query]);
  const importPet = async () => {
    if (!manifest || !sheet) return;
    setBusy(true);
    setMessage("");
    try {
      if (manifest.size > 1024 * 1024 || sheet.size > 32 * 1024 * 1024) throw new Error("配置文件上限 1 MB，图集上限 32 MB。");
      const result = validateLocalPetFiles(new Uint8Array(await manifest.arrayBuffer()), new Uint8Array(await sheet.arrayBuffer()));
      if (!result.ok || !result.manifest) throw new Error(result.issues.filter(issue => issue.severity === "error").map(issue => issue.message).join("；"));
      await saveLocalPet({ id: crypto.randomUUID(), manifest: result.manifest, spritesheet: sheet, tags: [] });
      await refreshPets();
      setMessage("已导入本机宠物库，点击使用即可切换。");
    } catch (cause) { setMessage(cause instanceof Error ? cause.message : "导入失败"); }
    finally { setBusy(false); }
  };
  const remove = async (pet: LocalPetRecord) => {
    if (!window.confirm(`从本机移除「${pet.manifest.displayName}」？原始导入文件会保留。`)) return;
    try {
      await deleteLocalPet(pet.id);
      if (activeId === pet.id) onSelect(null);
      await refreshPets();
    } catch { setMessage("移除失败，请重试。"); }
  };
  const signIn = async () => {
    if (!window.desktopPet) return;
    setBusy(true);
    setMessage("");
    try {
      const account = await window.desktopPet.signInSteam();
      setMessage(`已用 Steam 账号 ${account.name} 完成服务端验证。`);
    } catch (cause) {
      setMessage(cause instanceof Error ? cause.message : "Steam 登录失败");
    } finally {
      setBusy(false);
    }
  };
  const syncWorkshop = async () => {
    if (!window.desktopPet || !steam?.available) return;
    setBusy(true);
    setMessage("");
    try {
      const installed = await window.desktopPet.getWorkshopPets();
      let imported = 0;
      for (const item of installed) {
        const result = validateLocalPetFiles(item.manifest, item.spritesheet);
        if (!result.ok || !result.manifest) continue;
        const spritesheetCopy = new Uint8Array(item.spritesheet.byteLength);
        spritesheetCopy.set(item.spritesheet);
        await saveLocalPet({
          id: `steam-workshop:${item.itemId}`,
          manifest: result.manifest,
          spritesheet: new Blob([spritesheetCopy], { type: "image/webp" }),
          tags: ["steam-workshop"],
        });
        imported += 1;
      }
      await refreshPets();
      setMessage(`已同步 ${imported} 个通过校验的 Steam 工坊桌宠。`);
    } catch (cause) {
      setMessage(cause instanceof Error ? cause.message : "Steam 工坊同步失败");
    } finally {
      setBusy(false);
    }
  };
  const publishWorkshop = async () => {
    if (!window.desktopPet || !steam?.available || !workshopTitle.trim()) return;
    setBusy(true);
    setMessage("");
    try {
      const result = await window.desktopPet.publishWorkshopPet({
        title: workshopTitle,
        description: workshopDescription,
        itemId: publishedFileId.trim() || undefined,
      });
      setPublishedFileId(result.itemId);
      setMessage(result.needsToAcceptAgreement
        ? `作品 ${result.itemId} 已提交。请打开创意工坊接受 Steam Workshop 协议。`
        : `作品 ${result.itemId} 已作为私密工坊条目提交。`);
    } catch (cause) {
      setMessage(cause instanceof Error ? cause.message : "Steam 工坊发布失败");
    } finally {
      setBusy(false);
    }
  };
  return <div className="pet-manager">
    {window.desktopPet && <section className="steam-account" aria-label="Steam 账号状态">
      <div><small>STEAM · APP 5215690</small><h3>{steam?.available ? steam.name : "未连接 Steam"}</h3><p>{steam?.available ? `Steam ID ${steam.steamId} · Build ${steam.buildId ?? 0}` : (steam?.error ?? "正在读取 Steam 状态…")}</p></div>
      <div>{steam?.available && <><button disabled={busy} onClick={() => void signIn()}>验证 Steam 登录</button><button disabled={busy} onClick={() => void syncWorkshop()}>同步订阅</button></>}<button onClick={() => void window.desktopPet?.openSteamWorkshop()}>打开创意工坊</button></div>
    </section>}
    <div className="pet-manager-toolbar"><p>选择陪伴你的桌宠 · {pets.length + 1} 只</p><input aria-label="搜索桌宠" placeholder="搜索桌宠名称" value={query} onChange={event => setQuery(event.target.value)} /></div>
    <div className="pet-manager-grid">
      {(!query || "默认黑猫".includes(query)) && <article className="pet-manager-card"><div className="pet-manager-preview">🐈‍⬛</div><h3>默认黑猫</h3><p>内置桌宠</p><button onClick={() => onSelect(null)} disabled={!activeId}>{!activeId ? "正在使用" : "使用桌宠"}</button></article>}
      {filteredPets.map(pet => <article className="pet-manager-card" key={pet.id}><div className="pet-manager-preview"><LocalPetSprite pet={pet} /></div><h3>{pet.manifest.displayName}</h3><p>{pet.tags.includes("steam-workshop") ? "Steam 创意工坊" : "本地导入"}</p><div><button disabled={activeId === pet.id} onClick={() => onSelect(pet)}>{activeId === pet.id ? "正在使用" : "使用桌宠"}</button><button aria-label={`移除${pet.manifest.displayName}`} onClick={() => void remove(pet)}>移除</button></div></article>)}
    </div>
    {query && !pets.some(pet => pet.manifest.displayName.toLowerCase().includes(query.toLowerCase())) && !"默认黑猫".includes(query) && <p>没有匹配的桌宠。</p>}
    <section className="pet-manager-import"><h3>导入桌宠</h3><p>选择 v2 宠物配置和 WebP 图集，保存到本机。</p><label>pet.json<input type="file" accept=".json" onChange={event => setManifest(event.target.files?.[0] ?? null)} /></label><label>spritesheet.webp<input type="file" accept=".webp" onChange={event => setSheet(event.target.files?.[0] ?? null)} /></label><button disabled={busy || !manifest || !sheet} onClick={() => void importPet()}>{busy ? "正在导入…" : "导入到宠物库"}</button><p role="status">{message}</p></section>
    {window.desktopPet && <section className="pet-manager-publish"><h3>发布到 Steam 创意工坊</h3><p>选择包含 pet.json、spritesheet.webp 和 preview.png/jpg/gif 的文件夹。新作品始终先以“私密”状态创建。</p><label>标题<input value={workshopTitle} maxLength={128} onChange={event => setWorkshopTitle(event.target.value)} /></label><label>说明<textarea value={workshopDescription} maxLength={8000} onChange={event => setWorkshopDescription(event.target.value)} /></label><label>PublishedFileId（更新已有作品时填写）<input inputMode="numeric" value={publishedFileId} onChange={event => setPublishedFileId(event.target.value.replace(/\D/g, ""))} /></label><button disabled={busy || !steam?.available || !workshopTitle.trim()} onClick={() => void publishWorkshop()}>{publishedFileId ? "更新工坊作品" : "选择文件夹并发布"}</button></section>}
    <p className="pet-manager-note">Steam 已安装 {workshopItems.length} 个订阅作品；“同步订阅”只导入通过 v2 格式与图集尺寸校验的桌宠。</p>
  </div>;
}
